// Phase 28 (item C/D) — Archive view. The ONLY place hard-purge is reachable.
//
// Lists soft-archived cases, collective cases, tasks, agenda events and time
// entries (cases/CC are server-scoped to the viewer's visible set). Two actions
// per row:
//   * Restore → POST /:id/unarchive — reversible; the row returns to the live
//               list (pushed back into the parent ca/cc/tk/ag array).
//   * Purge   → DELETE /:id — hard delete. The server requires the row to be
//               archived first AND admin / isResponsiblePartner / can(.delete);
//               a 403 surfaces as a toast. A confirm gate guards it.
//
// SC3-R2 — archived tasks/events now surface here too (previously only cases +
// collective cases were queried, so an archive of a task/event showed "Nothing
// archived"). archived=true is persisted server-side by the archive action.
//
// ── 195 §A [RULED — Laurent, 2026-09-18] — AN ARCHIVED TASK OR EVENT IS THE
//    SAME CARD THE MATTER SHOWS, DIMMED ─────────────────────────────────────
// His words, at the 193 prod glance: "I can't know which event to restore
// since no date … put into archive the exact same items as in CV/CCV (the FULL
// CARDS of events/Tasks), so just reuse what exists, simpler" and "make them
// dimmed though so nobody confuses them with active tasks/events". MEASURED
// before a line was written: 193 §H's row printed the matter and the title
// and nothing else — no date, no time, no assignee, no priority, no subtasks.
// The two cards exist (CaseEventRow, CaseTaskRow: the components CaseAgendaSection
// and CaseTasksSection mount), and CaseEventRow already named a read-only
// idiom in its own props (`reduced` + agLocked). Both took an `archived` mode
// widening that idiom — the card as in the matter, every control inert, the
// one dimming (DIMMED_ROW_OPACITY, src/lib/tokens.ts) — and an
// `archivedActions` slot, which THIS screen fills with the archived-on line
// and its two actions. The task rows sit in the matter's own table under the
// matter's own header (TaskTableHead, moved out of CaseTasksSection for it).
// A TIME ENTRY IS NOT A CARD (A.5): the matter renders it as a LINE in
// TimeEntryTable — Date · Qui · Type · Description · Temps · Taux et frais ·
// Montant hors TVA — so the archived one keeps a line with those columns, its
// money from the same fee hook the matter's table calls (useEntryFees), and
// the same dimming. Cases and collective cases keep their present line (A.6).
// 193 §H's ruling stands — an archived task, event or time entry NAMES ITS
// MATTER — through the same resolver (191 §C's `resolveMatter`, over the live
// lists AND this screen's own archived matters); the name now sits where the
// card puts a matter: the event card's matter chip (the Dashboard's slot,
// inert here), the task row's case part before its title, the line's own
// column.

import React from "react"; // react/react-in-jsx-scope is counted per JSX element (169); the rule's own remedy
import { useEffect, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { RotateCcw, Search, Trash2 } from "lucide-react";
import {
  getArchivedCases,
  getArchivedCollectiveCases,
  getArchivedTasks,
  getArchivedAgenda,
  getArchivedTimeEntries,
  unarchiveCase,
  unarchiveCollectiveCase,
  unarchiveTask,
  unarchiveAgendaEvent,
  unarchiveTimeEntry,
  purgeCase,
  purgeCollectiveCase,
  purgeTask,
  purgeAgendaEvent,
  purgeTimeEntry,
} from "../api";
import { useTranslation } from "../i18n";
import type { TFn } from "../lib/taxonomyTypes";
import { LockButton } from "../components/SharedUI";
import { FS, DIMMED_ROW_OPACITY } from "../lib/tokens"; // 204 §7.1 — RAD left with the chip's copy
import { resolveMatter, matterEventTitle, type MatterRow } from "../lib/matterLabel"; // 193 §H — the sixteenth site of 191 §C's resolver
import CaseEventRow from "../components/CaseEventRow";
import CaseTaskRow from "../components/CaseTaskRow";
import { TaskTableHead } from "../components/CaseTasksSection";
import Table from "../components/Table";
import { TYPE_CHIP } from "../components/TypeTagBox"; // 204 §7.1 — the task-type chip's one definition
import { usePermissions } from "../hooks/usePermissions";
import { useRoleLabels } from "../hooks/useRoleLabels";
import { useEntryFees } from "../components/CountersStrip";
import { TeMarkPill } from "../components/TimeEntryTable"; // 195 §A.5 — the matter's own mark column
import { fc, fd, fmin, fq, pname } from "../utils";

// F5 — the purge button is RED (a real hard delete), distinct from the amber
// Archive icon used on live items. (`.bt-danger` is not defined in the CSS, so
// the red styling is applied inline here.)
const PURGE_STYLE = {
  background: "rgba(248,113,113,.15)",
  color: "var(--rd)",
  border: "1px solid rgba(248,113,113,.35)",
};

type ArchiveKind = "ca" | "cc" | "task" | "event" | "te";
/* what every archived row carries for this screen's own line and actions */
interface ArchivedRow {
  id: number;
  archivedAt?: string | null;
  archivedByName?: string | null;
  caseId?: number | null;
  caId?: number | null;
  ccId?: number | null;
}
// 152 §2 — an archived time entry as THIS screen reads it: the route's shaped
// row (alias keys and all), narrowed to the fields the line touches.
interface ArchivedTe extends ArchivedRow {
  desc?: string | null;
  description?: string | null;
  date?: string | null;
  coId?: number | null;
  type?: string | null;
  min?: number | null;
  invoiceId?: unknown;
  invoiceNumber?: string | null;
  mark?: string;
  billabilityMark?: string;
}
interface Person { id: number }
interface TaskType { code: string; desc?: string | null }
/* the served per-row money the matter's table reads (CountersStrip's EntryFeeRow) */
interface FeeRow { er?: number | null; wc?: number | null; cr?: number | null; stated?: boolean; derived?: number | null }

/* 193 §H — the matter an archived row names, resolved over the live lists AND
   this screen's own archived matters (an archived task on an archived case is
   the common case); "" when neither holds it, and the row reads as it did. */
const matterLabelOf = (r: ArchivedRow, cases: readonly MatterRow[], ccs: readonly MatterRow[]): string => {
  const matter = resolveMatter({ caseId: r.caseId ?? r.caId ?? null, ccId: r.ccId ?? null }, cases, ccs);
  return matter ? matterEventTitle(matter) : "";
};

const NO_EXPANSION: ReadonlySet<number> = new Set<number>();

/* 195 §C.3c [JUDGEMENT — advisor; Laurent's complaint was that he could not
   find an event, so a search that missed the date would not answer it] — THE
   ARCHIVE'S OWN SEARCH, over what the row SHOWS: the matter, the title (a
   case's ref and title, an entry's description), the type code, the date —
   as stored (2026-10-02) and as displayed (02/10/2026) — the time, the
   location, the status, the archived-on date. Case-folded substring, like
   every other box in the app. */
const norm = (v: unknown): string => (v == null ? "" : String(v)).toLowerCase();
const dateWords = (v: unknown): string[] => {
  if (!v) return [];
  const iso = String(v).slice(0, 10);
  return [iso, fd(iso)];
};
const rowHay = (kind: ArchiveKind, r: Record<string, unknown>, matter: string): string =>
  [
    matter,
    r.ref,
    r.title,
    r.desc,
    r.description,
    r.type,
    r.status,
    r.time,
    r.location,
    r.roomName,
    ...(kind === "task" ? dateWords(r.dueDate) : dateWords(r.date)),
    ...dateWords(r.archivedAt),
  ]
    .map(norm)
    .filter(Boolean)
    .join(" ");
const matches = (q: string, kind: ArchiveKind, r: Record<string, unknown>, matter: string): boolean =>
  !q || rowHay(kind, r, matter).includes(q);
const noop = () => undefined;
const todayStr = () => new Date().toISOString().slice(0, 10);

/* 195 §A — THE ROW'S OWN LINE AND ITS TWO ACTIONS, filled into the cards'
   `archivedActions` slot and rendered beside the cases' line: « archivé le … par
   … », then Restaurer and Supprimer définitivement (the purge behind its
   confirm gate), both behind the screen's lock. One definition for the five
   kinds. Hookless, module scope (Bug-A). The two acts arrive as tuples — the
   house's function-prop shape (`Dispatch<[…]>`, 194D). */
const ArchivedActions = ({ kind, row, confirmPurge, setConfirmPurge, purge, restore, t, archiveLocked }: Readonly<{
  kind: ArchiveKind;
  row: ArchivedRow;
  confirmPurge: string | null;
  setConfirmPurge: Dispatch<SetStateAction<string | null>>;
  purge: Dispatch<[ArchiveKind, ArchivedRow]>;
  restore: Dispatch<[ArchiveKind, ArchivedRow]>;
  t: TFn;
  archiveLocked: boolean;
}>) => {
  const ck = `${kind}:${String(row.id)}`;
  const confirming = confirmPurge === ck;
  /* two short lines at the row's right edge — the archived-on line over the
     buttons — so a task row's actions cell (capped by the table's global
     28-character cell width) never runs off the table: MEASURED at 1600, the
     one-line form clipped the buttons on every task row. */
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0, marginLeft: "auto" }}>
      {row.archivedAt && (
        <span style={{ color: "var(--tm)", fontSize: FS.s9_5, whiteSpace: "nowrap" }}>
          {t("archive.archived_on", { fallback: "archived" })} {String(row.archivedAt).slice(0, 10)}
          {row.archivedByName && (
            <>
              {" "}
              {t("archive.archived_by", { fallback: "by" })}{" "}
              <span style={{ color: "var(--ts)" }}>{row.archivedByName}</span>
            </>
          )}
        </span>
      )}
      {!archiveLocked && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <button type="button" className="btn" onClick={() => { restore([kind, row]); }} title={t("archive.restore", { fallback: "Restore" })}>
            <RotateCcw size={13} /> {t("archive.restore", { fallback: "Restore" })}
          </button>
          {confirming ? (
            <>
              <button type="button" className="btn" style={PURGE_STYLE} onClick={() => { purge([kind, row]); }}>
                {t("archive.purge_confirm", { fallback: "Delete permanently" })}
              </button>
              <button type="button" className="btn" onClick={() => { setConfirmPurge(null); }}>
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
            </>
          ) : (
            <button type="button" className="btn" style={PURGE_STYLE} onClick={() => { setConfirmPurge(ck); }} title={t("archive.purge", { fallback: "Delete permanently" })}>
              <Trash2 size={13} />
            </button>
          )}
        </span>
      )}
    </span>
  );
};

/* A.6 — a case or a collective case keeps its present line: the ref, the
   title, click to open; its actions are the shared piece above. */
const MatterLine = ({ kind, row, nav, t, actions }: Readonly<{
  kind: "ca" | "cc";
  row: ArchivedRow & { ref?: string | null; title?: string | null };
  nav: (kind: string, id: number) => void;
  t: TFn;
  actions: ReactNode;
}>) => (
  <div className="TR" style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderBottom: "1px solid var(--br)" }}>
    <span
      style={{ flex: 1, cursor: "pointer", color: "var(--tp)", fontSize: FS.s12 }}
      onClick={() => { nav(kind, row.id); }}
      title={t("archive.open", { fallback: "Open" })}
    >
      <strong>{row.ref ?? "—"}</strong>
      {" — "}
      {row.title ?? t("archive.untitled", { fallback: "(untitled)" })}
    </span>
    {actions}
  </div>
);

/* A.5 — the archived time entry's LINE: the matter's own columns (TimeEntryTable,
   158 §7's order), the matter's name in its own column (193 §H), the served
   money through the same fee hook, dimmed by the one definition. Hookless. */
const TeLine = ({ e, matter, co, tkTypes, fee, t, actions }: Readonly<{
  e: ArchivedTe;
  matter: string;
  co: readonly Person[];
  tkTypes: readonly TaskType[];
  fee: FeeRow | undefined;
  t: TFn;
  actions: ReactNode;
}>) => {
  const c = co.find((x) => x.id === e.coId);
  const tk = tkTypes.find((x) => x.code === e.type);
  const desc = e.desc ?? e.description ?? "";
  return (
    <tr style={{ opacity: DIMMED_ROW_OPACITY, cursor: "default" /* the stylesheet's `tbody tr { cursor: pointer }` promises nothing here */ }}>
      <td className="mono">{fd(e.date)}</td>
      <td><span className="name-cell">{c ? pname(c) : "—"}</span></td>
      <td>
        {e.type ? (
          <span
            title={tk?.desc ?? undefined}
            /* 204 §7.1 — the chip's ONE definition (TypeTagBox's TYPE_CHIP)
               in place of this row's own copy (`1px 5px`, a transparent
               border, `--ac`): the archived entry's type wears what every
               task row wears — a visible change, the repoint's own. */
            style={TYPE_CHIP}
          >
            {e.type}
          </span>
        ) : (
          <span style={{ color: "var(--tm)" }}>—</span>
        )}
      </td>
      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={matter}>{matter || "—"}</td>
      <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={desc}>{desc || "—"}</td>
      <td className="mono">{fmin(e.min)}</td>
      <td className="mono" style={{ whiteSpace: "nowrap", ...(fee?.stated ? { color: "var(--st)" } : {}) }}>
        {fq(fee ? fee.er : null)} €/h
        {fee && typeof fee.cr === "number" && <span style={{ marginLeft: 4 }}>+ {fee.cr} %</span>}
      </td>
      <td className="mono">{fee && (fee.er ?? 0) > 0 && typeof fee.wc === "number" ? fc(fee.wc) : "—"}</td>
      <td style={{ whiteSpace: "nowrap" }}><TeMarkPill e={e} t={t} /></td>
      {/* the actions cell escapes the table's global cell cap (`tbody td { max-width: 28ch; overflow: hidden }`) */}
      <td style={{ whiteSpace: "nowrap", maxWidth: "none", overflow: "visible" }}>{actions}</td>
    </tr>
  );
};

/* 200 §D.1 [RULED — Laurent, 2026-09-20] — THE HEADING KEEPS ITS DISTANCE
   FROM ITS TABLE. MEASURED: this `<h3>` set its size and nothing else, and the
   app's universal reset (`* { margin: 0 }`, App.css:124) leaves a bare heading
   flush against what follows — 0 px between « Dossiers (3) » and the table's
   first row, on all five subsections. Every other section heading in the app
   sets its own: `PANE_TITLE` is `margin: "0 0 8px"`, CostsView's `SECTION_H3`
   is `marginBottom: 8` — so this one takes the same 8 px, here, once, and the
   five mounts (cases · collective cases · tasks · events · time entries) move
   together. */
const Section = ({ title, count, children }: Readonly<{ title: string; count: number; children: ReactNode }>) =>
  count > 0 ? (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: FS.s13, margin: "0 0 8px" }}>
        {title} ({count})
      </h3>
      {children}
    </div>
  ) : null;

/* The props App hands this screen. The card-rendering ones (`etTag`, `coAvs`,
   `locIcon`, `TkStatusTag`, …) are App's own closures, passed through untouched
   to the two cards whose props they are; typed `unknown` at this seam so no
   `any` is written here. */
interface Props {
  sCa: Dispatch<SetStateAction<unknown[]>>;
  sCc: Dispatch<SetStateAction<unknown[]>>;
  sTk: Dispatch<SetStateAction<unknown[]>>;
  sAg: Dispatch<SetStateAction<unknown[]>>;
  sTe: Dispatch<SetStateAction<unknown[]>>;
  nav: unknown;
  toast: unknown;
  ca: readonly MatterRow[];
  cc: readonly MatterRow[];
  co: unknown;
  currentUser: unknown;
  tkTypes: unknown;
  evtTypes: unknown;
  etTag: unknown;
  evtLongestCode: string;
  coAvs: unknown;
  locIcon: unknown;
  priMap: unknown;
  itemLevelList: unknown;
  TkStatusTag: unknown;
  /** 195 §B.0 — Restaurer on an EVENT: App opens the event modal prefilled
   *  from the row; `done` fires with the restored row once the save has
   *  un-archived it (a tuple: the house's function-prop shape) */
  restoreEvent: Dispatch<[ArchivedRow, Dispatch<unknown>]>;
}

export default function ArchiveView({ sCa, sCc, sTk, sAg, sTe, nav: navIn, toast: toastIn, ca, cc, co: coIn, currentUser: meIn, tkTypes: tkTypesIn, evtTypes, etTag, evtLongestCode, coAvs, locIcon, priMap, itemLevelList, TkStatusTag, restoreEvent }: Readonly<Props>) {
  const { t } = useTranslation() as { t: TFn };
  /* App's own shapes, narrowed at this seam (a cast, not a read off `any`) */
  const nav = navIn as (kind: string, id: number) => void;
  const toast = toastIn as (m: string) => void;
  const co = (Array.isArray(coIn) ? coIn : []) as readonly Person[];
  const tkTypes = (Array.isArray(tkTypesIn) ? tkTypesIn : []) as readonly TaskType[];
  const currentUser = (typeof meIn === "number" ? meIn : null) as number | null;
  /* 190 §F.2 — the furniture keys, the two rows' own predicate, for the
     tasks table's header (a hidden column goes header and cells together) */
  const perms = usePermissions();
  const fur = (k: string): boolean => perms.viewer === null || perms.can(k);
  const [aCases, setACases] = useState<(ArchivedRow & { ref?: string | null; title?: string | null })[]>([]);
  const [aCcs, setACcs] = useState<(ArchivedRow & { ref?: string | null; title?: string | null })[]>([]);
  const [aTasks, setATasks] = useState<(ArchivedRow & { priority?: string | null })[]>([]);
  const [aEvents, setAEvents] = useState<ArchivedRow[]>([]);
  const [aTes, setATes] = useState<ArchivedTe[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmPurge, setConfirmPurge] = useState<string | null>(null); // `${kind}:${id}`
  // F6 — view-vs-edit lock. The archived list is always visible; restore and
  // permanent-delete are gated behind unlock (the purge authority — RP /
  // delete-grant — still applies underneath; this is an additional UI gate).
  const [archiveLocked, setArchiveLocked] = useState(true);
  /* 195 §C.3c — the screen's own search text */
  const [search, setSearch] = useState("");

  const reload = () => {
    setLoading(true);
    const arr = <T,>(r: PromiseSettledResult<unknown>): T[] =>
      r.status === "fulfilled" && Array.isArray(r.value) ? (r.value as T[]) : [];
    Promise.allSettled([
      getArchivedCases(),
      getArchivedCollectiveCases(),
      getArchivedTasks(),
      getArchivedAgenda(),
      getArchivedTimeEntries(),
    ])
      .then(([rc, rcc, rt, re, rte]) => {
        setACases(arr(rc));
        setACcs(arr(rcc));
        setATasks(arr(rt));
        setAEvents(arr(re));
        setATes(arr(rte));
      })
      .finally(() => { setLoading(false); });
  };
  useEffect(reload, []);

  const localSetter: Record<ArchiveKind, Dispatch<SetStateAction<never[]>>> = {
    ca: setACases as Dispatch<SetStateAction<never[]>>,
    cc: setACcs as Dispatch<SetStateAction<never[]>>,
    task: setATasks as Dispatch<SetStateAction<never[]>>,
    event: setAEvents as Dispatch<SetStateAction<never[]>>,
    te: setATes as Dispatch<SetStateAction<never[]>>,
  };
  const liveSetter: Record<ArchiveKind, Dispatch<SetStateAction<unknown[]>>> = { ca: sCa, cc: sCc, task: sTk, event: sAg, te: sTe };
  const unarchiveFn: Record<ArchiveKind, (id: number) => Promise<unknown>> = {
    ca: unarchiveCase,
    cc: unarchiveCollectiveCase,
    task: unarchiveTask,
    event: unarchiveAgendaEvent,
    te: unarchiveTimeEntry,
  };
  const purgeFn: Record<ArchiveKind, (id: number) => Promise<unknown>> = {
    ca: purgeCase,
    cc: purgeCollectiveCase,
    task: purgeTask,
    event: purgeAgendaEvent,
    te: purgeTimeEntry,
  };
  const dropLocal = (kind: ArchiveKind, id: number) => {
    localSetter[kind]((p: readonly ArchivedRow[]) => p.filter((x) => x.id !== id) as never[]);
  };

  const restore = async ([kind, row]: [ArchiveKind, ArchivedRow]) => {
    /* 195 §B.0/B.1 [RULED] — an EVENT restores through its modal: the
       archived row is handed to App, the modal opens prefilled, and this
       screen drops the row only when the save has un-archived it (`done`).
       Cancel leaves it here, archived, untouched. The four other kinds
       restore in one click, as before. */
    if (kind === "event") {
      restoreEvent([row, () => { dropLocal("event", row.id); }]);
      return;
    }
    try {
      const restored = await unarchiveFn[kind](row.id);
      dropLocal(kind, row.id);
      // Push back into the live list so it reappears without a full reload.
      liveSetter[kind]((prev) =>
        Array.isArray(prev)
          ? prev.some((x) => (x as ArchivedRow).id === row.id)
            ? prev
            : [...prev, restored ?? row]
          : prev,
      );
      toast(t("archive.restored", { fallback: "Restored" }));
    } catch (e: unknown) {
      toast((e as { message?: string } | null)?.message ?? t("archive.restore_failed", { fallback: "Restore failed" }));
    }
  };

  const purge = async ([kind, row]: [ArchiveKind, ArchivedRow]) => {
    try {
      await purgeFn[kind](row.id);
      dropLocal(kind, row.id);
      setConfirmPurge(null);
      toast(t("archive.purged", { fallback: "Permanently deleted" }));
    } catch (e: unknown) {
      setConfirmPurge(null);
      toast((e as { message?: string } | null)?.message ?? t("archive.purge_failed", { fallback: "Delete failed" }));
    }
  };

  /* 193 §H — the matters an archived row may name: the live lists App holds
     and the archived ones this screen has just read. Typed at the seam. */
  const matterCases: readonly MatterRow[] = [...ca, ...(aCases as unknown as MatterRow[])];
  const matterCcs: readonly MatterRow[] = [...cc, ...(aCcs as unknown as MatterRow[])];
  const labelOf = (r: ArchivedRow) => matterLabelOf(r, matterCases, matterCcs);

  /* A.5 — the archived entries' money, through the matter table's own hook:
     the row's matter resolved over the same two lists (the fee needs the
     matter's rate and costs ratio, which an archived case still carries). */
  const { rows: roleRows } = useRoleLabels();
  const teMatter = (e: ArchivedTe) =>
    (e.ccId != null ? matterCcs.find((m) => m.id === e.ccId) : matterCases.find((m) => m.id === (e.caId ?? e.caseId))) ?? null;
  const feeCalc = useEntryFees({ entries: aTes, caseOf: (e: unknown) => teMatter(e as ArchivedTe), co, roleRows });

  const actionsFor = (kind: ArchiveKind, row: ArchivedRow) => (
    <ArchivedActions kind={kind} row={row} confirmPurge={confirmPurge} setConfirmPurge={setConfirmPurge} purge={(a) => { void purge(a); }} restore={(a) => { void restore(a); }} t={t} archiveLocked={archiveLocked} />
  );

  const empty =
    !loading &&
    aCases.length === 0 &&
    aCcs.length === 0 &&
    aTasks.length === 0 &&
    aEvents.length === 0 &&
    aTes.length === 0;
  const today = todayStr();
  /* 195 §C.3c — what the box narrows: every kind, over what its row shows */
  const q = search.trim().toLowerCase();
  const shownCases = aCases.filter((r) => matches(q, "ca", r as unknown as Record<string, unknown>, ""));
  const shownCcs = aCcs.filter((r) => matches(q, "cc", r as unknown as Record<string, unknown>, ""));
  const shownTasks = aTasks.filter((r) => matches(q, "task", r as unknown as Record<string, unknown>, labelOf(r)));
  const shownEvents = aEvents.filter((r) => matches(q, "event", r as unknown as Record<string, unknown>, labelOf(r)));
  const shownTes = aTes.filter((r) => matches(q, "te", r as unknown as Record<string, unknown>, labelOf(r)));
  const nothingMatches = !loading && !empty && q !== "" && shownCases.length + shownCcs.length + shownTasks.length + shownEvents.length + shownTes.length === 0;
  const tasksShowLevel = shownTasks.some((x) => !!x.priority);

  return (
    <div className="vw" style={{ padding: 16 }}>
      {/* 195B §D [RULED — Laurent, 2026-09-19] — THE TITLE IS PRINTED ONCE.
          An `<h2>` stood here printing « 🗄 Archives » under the page header
          that already prints « Archives » (App.tsx, `curL` off the nav label —
          the same `nav.archive` key). PRE-EXISTING, NOT 195's: it is in
          Laurent's screenshots from before 195 was written. It also held the
          lock at its right edge, which is a control and stays: it moves onto
          the search row below, at the right edge, where every party list
          keeps its lock (App.tsx, the lists' actions row). MEASURED before it
          went: no shared block — FleetView prints the same doubled heading
          from its own three `<h2>`s (cockpit-only; surfaced, not this
          screen's). */}
      {/* 195 §C.3c [RULED C.0] — the Archive owns its search box, over the
          cards §A renders: matter, title, type code, date (158 §8: the box
          leads what it filters). 195B §D — the row now also carries the lock
          (right edge), so it renders even on an empty archive, where the box
          alone did not. 200 §D.2 [RULED — Laurent, 2026-09-20: "put the
          explanation that is now above the search box, next to it in same
          row, at some distance"] — THE INTRO JOINS THIS ROW, after the box,
          16 px beyond the row's own 8 (24 px of air between the box and the
          first word), the lock still at the right edge. It renders on an EMPTY
          archive too (the box does not; the row does — the intro then leads
          it), and the row keeps wrapping: `flex: 1 1 360px` is the 166 §I.3
          mechanism — beside the box it takes the spare room up to its 640 px,
          and where 240 + the text's 360 do not fit it drops to its own line
          rather than crushing the box or being crushed by it. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {!empty && (
          <div className="search-box" style={{ width: 240 }}>
            <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
            <input
              placeholder={t("common.search_placeholder")}
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
            />
          </div>
        )}
        <p style={{ color: "var(--tm)", fontSize: FS.s11, maxWidth: 640, flex: "1 1 360px", minWidth: 0, margin: 0, marginLeft: empty ? 0 : 16 }}>
          {t("archive.intro", {
            fallback:
              "Archived items are hidden from the live lists but fully recoverable. Restore returns an item to active use. Permanent deletion is irreversible.",
          })}
        </p>
        <span style={{ marginLeft: "auto" }}>
          <LockButton locked={archiveLocked} onToggle={() => { setArchiveLocked((v) => !v); }} />
        </span>
      </div>

      {loading && <p style={{ color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</p>}
      {empty && <p style={{ color: "var(--tm)" }}>{t("archive.empty", { fallback: "Nothing archived." })}</p>}
      {nothingMatches && <p style={{ color: "var(--tm)" }}>{t("archive.no_match", { fallback: "Nothing archived matches this search." })}</p>}

      <Section title={t("nav.cases", { fallback: "Cases" })} count={shownCases.length}>
        <div className="table-wrap">
          {shownCases.map((r) => <MatterLine key={`ca${String(r.id)}`} kind="ca" row={r} nav={nav} t={t} actions={actionsFor("ca", r)} />)}
        </div>
      </Section>
      <Section title={t("nav.collective_cases", { fallback: "Collective cases" })} count={shownCcs.length}>
        <div className="table-wrap">
          {shownCcs.map((r) => <MatterLine key={`cc${String(r.id)}`} kind="cc" row={r} nav={nav} t={t} actions={actionsFor("cc", r)} />)}
        </div>
      </Section>
      <Section title={t("archive.tasks", { fallback: "Tasks" })} count={shownTasks.length}>
        {/* the matter's own table: its header (the furniture keys, no sort), its row in the archived mode */}
        <div className="table-wrap">
          <Table>
            <TaskTableHead t={t} fur={fur} showLevel={tasksShowLevel} />
            <tbody>
              {shownTasks.map((r) => (
                <CaseTaskRow
                  key={r.id}
                  t={r}
                  archived
                  archivedActions={actionsFor("task", r)}
                  caseLabel={labelOf(r)}
                  co={co}
                  currentUser={currentUser}
                  tk={aTasks}
                  tkTypes={tkTypes}
                  itemLevelList={itemLevelList}
                  priMap={priMap}
                  activeTimers={[]}
                  tkLocked
                  tkExpanded={NO_EXPANSION}
                  setTkExpanded={noop}
                  TkStatusTag={TkStatusTag}
                  startTimer={noop}
                  svTkData={noop}
                  sTk={noop}
                  openModal={noop}
                  onDeleteTask={noop}
                  showLevel={tasksShowLevel}
                />
              ))}
            </tbody>
          </Table>
        </div>
      </Section>
      <Section title={t("archive.events", { fallback: "Events" })} count={shownEvents.length}>
        {/* the matter's own card, in the archived mode; the matter in the card's own chip slot, inert */}
        <div>
          {shownEvents.map((r) => {
            const label = labelOf(r);
            return (
              <CaseEventRow
                key={r.id}
                ev={r}
                archived
                archivedActions={actionsFor("event", r)}
                co={co}
                currentUser={currentUser}
                todayStr={today}
                priMap={priMap}
                itemLevelList={itemLevelList}
                evtTypes={evtTypes}
                etTag={etTag}
                evtLongestCode={evtLongestCode}
                coAvs={coAvs}
                locIcon={locIcon}
                evExpanded={NO_EXPANSION}
                toggleEvExpanded={noop}
                agLocked
                matterChip={label ? { label, title: label, full: true } : undefined}
              />
            );
          })}
        </div>
      </Section>
      {/* 152 §2 — the fifth kind. `case.time_entries` is the app's own name for
          this list and was already seeded en/fr/nl, so this section adds no
          i18n row and cannot render an English fallback on a French screen. */}
      <Section title={t("case.time_entries", { fallback: "Time Entries" })} count={shownTes.length}>
        <div className="table-wrap">
          <Table className="teTable teTable-page" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>{t("common.date", { fallback: "Date" })}</th>
                <th>{t("te.col.who", { fallback: "Who" })}</th>
                <th>{t("common.type", { fallback: "Type" })}</th>
                <th>{t("field.case", { fallback: "Case" })}</th>
                <th>{t("te.col.desc", { fallback: "Description" })}</th>
                <th>{t("te.col.time", { fallback: "Time" })}</th>
                <th>{t("te.col.rate_costs", { fallback: "Rate and costs" })}</th>
                <th>{t("te.col.amt_ex_vat", { fallback: "Amount without VAT" })}</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shownTes.map((e) => (
                <TeLine key={e.id} e={e} matter={labelOf(e)} co={co} tkTypes={tkTypes} fee={feeCalc ? (feeCalc[String(e.id)] as FeeRow | undefined) : undefined} t={t} actions={actionsFor("te", e)} />
              ))}
            </tbody>
          </Table>
        </div>
      </Section>
    </div>
  );
}
